import { useState, useMemo, useCallback } from 'react';

function formatDateTime(iso) {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit'
  });
}

function DataPage({ timeSeries, hauls, wellName, tankLabel, bblPerIn }) {
  const [tab, setTab] = useState('timeseries');
  const [tsPage, setTsPage] = useState(0);
  const pageSize = 200;

  const tsRows = useMemo(() => {
    return timeSeries.map(pt => ({
      time: pt.time,
      level: pt.level,
    }));
  }, [timeSeries]);

  const totalTsPages = Math.ceil(tsRows.length / pageSize);
  const pagedTs = useMemo(() => {
    const start = tsPage * pageSize;
    return tsRows.slice(start, start + pageSize);
  }, [tsRows, tsPage]);

  const downloadCsv = useCallback((rows, columns, filename) => {
    const header = columns.map(c => c.label).join(',');
    const body = rows.map(r => columns.map(c => {
      const val = c.accessor(r);
      if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
      return val ?? '';
    }).join(',')).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const tsCols = [
    { label: 'Timestamp', accessor: r => r.time },
    { label: 'Level (in)', accessor: r => r.level?.toFixed(1) ?? '' },
    ...(bblPerIn > 0 ? [{ label: 'Level (bbl)', accessor: r => r.level != null ? (r.level * bblPerIn).toFixed(1) : '' }] : []),
  ];

  const haulCols = [
    { label: 'Drop Start', accessor: r => r.drop_start },
    { label: 'Drop End', accessor: r => r.drop_end },
    { label: 'Level Before (in)', accessor: r => r.level_before_ft?.toFixed(1) ?? '' },
    { label: 'Level After (in)', accessor: r => r.level_after_ft?.toFixed(1) ?? '' },
    { label: 'Haul Volume (in)', accessor: r => r.haul_volume_ft?.toFixed(1) ?? '' },
    ...(bblPerIn > 0 ? [{ label: 'Haul Volume (bbl)', accessor: r => r.haul_volume_ft != null ? (r.haul_volume_ft * bblPerIn).toFixed(1) : '' }] : []),
    { label: 'Fill Rate Before (in/hr)', accessor: r => r.pre_slope_ft_per_hr?.toFixed(3) ?? '' },
    { label: 'Fill Rate After (in/hr)', accessor: r => r.post_slope_ft_per_hr?.toFixed(3) ?? '' },
    ...(bblPerIn > 0 ? [{
      label: 'Water Rate (bwpd)',
      accessor: r => r.post_slope_ft_per_hr > 0 ? (r.post_slope_ft_per_hr * bblPerIn * 24).toFixed(1) : ''
    }] : []),
    { label: 'Gas Rate (mcf/d)', accessor: r => r.gas_rate_mcf_day?.toFixed(1) ?? '' },
    ...(bblPerIn > 0 ? [{
      label: 'WGR (bbl/mcf)',
      accessor: r => {
        if (!r.post_slope_ft_per_hr || r.post_slope_ft_per_hr <= 0 || !r.gas_rate_mcf_day || r.gas_rate_mcf_day <= 0) return '';
        return ((r.post_slope_ft_per_hr * bblPerIn * 24) / r.gas_rate_mcf_day).toFixed(3);
      }
    }] : []),
    { label: 'Pre R²', accessor: r => isFinite(r.pre_r_squared) ? r.pre_r_squared.toFixed(2) : '' },
    { label: 'Post R²', accessor: r => isFinite(r.post_r_squared) ? r.post_r_squared.toFixed(2) : '' },
  ];

  const safeName = (wellName || 'well').replace(/[^a-zA-Z0-9_-]/g, '_');

  return (
    <div>
      <div className="data-page-header">
        <div className="data-page-tabs">
          <button
            className={`data-tab ${tab === 'timeseries' ? 'active' : ''}`}
            onClick={() => setTab('timeseries')}
          >
            Time Series ({tsRows.length.toLocaleString()} points)
          </button>
          <button
            className={`data-tab ${tab === 'hauls' ? 'active' : ''}`}
            onClick={() => setTab('hauls')}
          >
            Haul Events ({hauls.length})
          </button>
        </div>
        <button
          className="quick-btn"
          onClick={() => {
            if (tab === 'timeseries') {
              downloadCsv(tsRows, tsCols, `${safeName}_${tankLabel}_timeseries.csv`);
            } else {
              downloadCsv(hauls, haulCols, `${safeName}_${tankLabel}_hauls.csv`);
            }
          }}
        >
          Download CSV
        </button>
      </div>

      {tab === 'timeseries' && (
        <div className="haul-table-card">
          <div className="haul-table-wrap" style={{ maxHeight: 500 }}>
            <table>
              <thead>
                <tr>
                  {tsCols.map(c => <th key={c.label}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {pagedTs.map((r, i) => (
                  <tr key={i}>
                    {tsCols.map(c => <td key={c.label}>{c.accessor(r)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalTsPages > 1 && (
            <div className="data-pagination">
              <button
                className="quick-btn"
                disabled={tsPage === 0}
                onClick={() => setTsPage(p => p - 1)}
              >
                Prev
              </button>
              <span className="data-page-info">
                Page {tsPage + 1} of {totalTsPages}
                {' · '}Showing {tsPage * pageSize + 1}–{Math.min((tsPage + 1) * pageSize, tsRows.length)} of {tsRows.length.toLocaleString()}
              </span>
              <button
                className="quick-btn"
                disabled={tsPage >= totalTsPages - 1}
                onClick={() => setTsPage(p => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'hauls' && (
        <div className="haul-table-card">
          <div className="haul-table-wrap" style={{ maxHeight: 500 }}>
            <table>
              <thead>
                <tr>
                  {haulCols.map(c => <th key={c.label}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {hauls.map((h, i) => (
                  <tr key={i}>
                    {haulCols.map(c => <td key={c.label}>{c.accessor(h)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataPage;
